import React from 'react';
import './libs/lexical/plugins/mention/MentionsPlugin.scss';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import ability from "./ability";
import { AbilityContext } from './ability-context';
import { FiltersLocalProvider } from './hooks/useFiltersLocal';
import { FiltersProvider } from './hooks/useFiltersStorage';
import { BreakpointProvider } from "./libs/breakpoint";
import history from './libs/history';
import Aqp from './libs/mk-react-aqp';
import { initPwaDisplayMode } from './libs/pwa';
import { initTheme } from './libs/theme';
import RootRoutes from './routes';
import './sass/master.scss';
import './component-styles';
import AlertModal from "./ui/components2/AlertModal/AlertModal";
import { DebugProvider } from './ui/components2/Debug';
import ModalManager from './ui/components2/ModalManager';
import { Signin } from "./ui/components2/Signin/Signin";
import { Signup } from "./ui/components2/Signup/Signup";
import { DrawerProvider } from './ui/components2/DrawerProvider';
import { SocketProvider } from './hooks/useSocket';
import AgeConfirmation from './ui/components2/AgeConfirmation/AgeConfirmation';
import { PostHogProvider} from 'posthog-js/react'
import EncourageSignup from './ui/components2/Encourage/EncourageSignup/EncourageSignup';
import ModalUserRestrictions from './ui/components2/Restrictions/ModalUserRestrictions';
import EncourageEmailConfirm from './ui/components2/Encourage/EncourageEmailConfirm/EncourageEmailConfirm';
import StoryViewersModal from './ui/components2/Stories/StoryViewersModal/StoryViewersModal';
import OffersModalGlobal from './ui/components2/OffersModal/OffersModalGlobal';
import StoryLikersModal from './ui/components2/Stories/StoryViewersModal/StoryLikersModal';
import StoryRepliesModal from './ui/components2/Stories/StoryReplies/StoryRepliesModal';
import { NotifierProvider } from './ui/components2/UI/Notifier/Notifier';
import { DrimifyProvider } from './libs/drimify/DrimifyProvider';
import useToken from './hooks/token';
import PublicationCurfewModal from './ui/components2/PublicationCurfewModal/PublicationCurfewModal';
import PublicationCurfewListener from './ui/components2/PublicationCurfewModal/PublicationCurfewListener';
import { emitPublicationCurfew, isPublicationCurfewError } from './libs/publicationCurfew';
import ForumCertificationModal from './ui/components2/ForumCertificationModal/ForumCertificationModal';
import AcceptanceModal from './ui/components2/AcceptanceModal/AcceptanceModal';
import ForumCertificationListener from './ui/components2/ForumCertificationModal/ForumCertificationListener';
import AcceptanceListener from './ui/components2/AcceptanceModal/AcceptanceListener';
import { emitForumCertification, isForumCertificationError } from './libs/forumCertification';
import { isAcceptanceError, getAcceptanceErrorType, emitAcceptance } from './libs/userAcceptance';

// Apply persisted theme (light/dark/auto) avant le render React pour éviter le flash
initTheme();

// Marque <html> quand l'app tourne en PWA installée : conditionne le pull-to-refresh maison
// et la neutralisation du rafraîchissement natif (cf. PullToRefresh).
initPwaDisplayMode();

const SocketWrapper = ({ children }) => {
    const [token] = useToken();
    const hasValidToken = !!token && !["undefined", "null"].includes(String(token).toLowerCase());
    return (
        <SocketProvider token={hasValidToken ? token : null}>
            {children}
        </SocketProvider>
    );
};


Aqp.init(history);

const queries = {
    xs: '(max-width: 320px)',
    sm: '(max-width: 720px)',
    md: '(max-width: 1024px)',
    lg: '(max-width: 1200px)',
    or: '(orientation: portrait)'
}


const modalTypes = {
    EncourageSignup,
    EncourageEmailConfirm,
    AlertModal,
    Signin,
    Signup,
    AgeConfirmation,
    ModalUserRestrictions,
    StoryViewersModal,
    StoryLikersModal,
    StoryRepliesModal,
    // Enregistrée en modale GLOBALE : le ModalManager est monté AU-DESSUS du DrawerProvider dans
    // l'arbre, donc elle se superpose à un drawer ouvert au lieu de le remplacer. Indispensable
    // pour l'upsell déclenché DEPUIS un drawer (ex. durée 48 h dans le panneau de création) :
    // ouvrir un second drawer y écraserait le panneau et ferait perdre le fichier déjà envoyé.
    OffersModal: OffersModalGlobal,
    // Couvre-feu de publication (20h → 8h) : globale car déclenchée depuis l'en-tête, le feed,
    // les éditeurs, et par le filet de sécurité sur échec de mutation (cf. mutationCache).
    PublicationCurfewModal,
    // Même raison : déclenchée depuis plusieurs pages du forum, et par le filet global sur
    // refus de l'API.
    ForumCertificationModal,
    // Acceptation de la charte : globale car déclenchée depuis tous les points de publication
    // (uploader, éditeurs de commentaire) et par le filet global sur refus de l'API.
    AcceptanceModal
};

const container = document.getElementById('root');
const root = createRoot(container);

const statusCodeErrorNoRetry = [400, 401, 403, 404, 423, 500];

function handleNetworkError(error) {
    if (window.location.pathname !== '/maintenance') {
        if (window.plausible) {
            const fullURL = new URL(error?.config.url, error?.config.baseURL);
            window.plausible('ERR_NETWORK', {
                props: {
                    url: error?.config.url,
                    path: fullURL.pathname,
                    search: fullURL.search,
                    method: error?.config.method
                }
            });
            console.warn(`ERR_NETWORK ${error?.config.method} ${error?.config.url}`);
        }
        sessionStorage.setItem('maintenance-backup-path', window.location.pathname);
        console.warn("Network Error: Redirecting to maintenance page.");
        window.location.href = '/maintenance';
    }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        console.warn(`[QUERY RETRY] Attempt #${count} – ${error?.config?.method} ${error?.config?.url}`);
        const statusCode =  error?.statusCode;
        return !statusCodeErrorNoRetry.includes(statusCode) && count < 2;
      },
      retryDelay: (attempt) => [1000, 3000, 5000][attempt] ?? 7000,
      onError: (error) => {
        if (error?.code === "ERR_NETWORK") {
          handleNetworkError(error);
        }
      }
    },
    // mutations: {
    //   retry: (count, error) => {
    //     console.warn(`[MUTATION RETRY] Attempt #${count} – ${error?.config?.method} ${error?.config?.url}`);
    //     const statusCode = error?.statusCode;
    //     return !statusCodeErrorNoRetry.includes(statusCode) && count < 1;
    //   },
    //   retryDelay: (attempt) => [1500, 3000, 5000][attempt] ?? 7000,
    //   onError: (error) => {
    //     if (error?.code === "ERR_NETWORK") {
    //       handleNetworkError(error);
    //     }
    //   }
    // }
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error?.statusCode === 403) {
        window.location.href = "/";
      }
    }
  }),
  // Filets de sécurité du couvre-feu de publication et du forum réservé aux certifiés
  // UNIQUEMENT. Volontairement pas de règle
  // générique sur les 403 ici : contrairement aux queries ci-dessus, un 403 de mutation est une
  // action refusée, pas une page interdite — rediriger ferait perdre la saisie en cours.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isPublicationCurfewError(error)) {
        emitPublicationCurfew(error?.response?.errors?.curfew || error?.errors?.curfew);
      }
      if (isForumCertificationError(error)) {
        emitForumCertification();
      }
      if (isAcceptanceError(error)) {
        emitAcceptance(getAcceptanceErrorType(error));
      }
    }
  })
});


root.render(
    <PostHogProvider 
        apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
        options={{
            api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
            person_profiles: 'identified_only'
        }}
        >
            
        <QueryClientProvider client={queryClient}>
            {process.env.NODE_ENV === "developmesnt" ?  <ReactQueryDevtools initialIsOpen={false} /> : null}
            <FiltersProvider>
                <FiltersLocalProvider>
                    <BrowserRouter>
                        <DebugProvider>
                            <BreakpointProvider queries={queries}>
                                <AbilityContext.Provider value={ability}>
                                    <DrimifyProvider>
                                        <ModalManager modalTypes={modalTypes}>
                                            <PublicationCurfewListener />
                                            <ForumCertificationListener />
                                            <AcceptanceListener />
                                            <NotifierProvider>
                                                <DrawerProvider>
                                                    <SocketWrapper>
                                                        <RootRoutes />
                                                    </SocketWrapper>
                                                </DrawerProvider>
                                            </NotifierProvider>
                                        </ModalManager>
                                    </DrimifyProvider>
                                </AbilityContext.Provider>
                            </BreakpointProvider>
                        </DebugProvider>
                        
                    </BrowserRouter>
                </FiltersLocalProvider>
            </FiltersProvider>
        </QueryClientProvider>
    </PostHogProvider>
);
